/* ============================================================
   THE SOLE STORE — Bespoke PDP  (Horizon theme)
   assets/solestore-pdp.js
   Vanilla JS, no dependencies. Handles: size/variant selection,
   price + CTA sync, sticky checkout bar, recommendations.
   (Accordions use native <details>; ticker/reviews are Phase 2.)

   CHANGE LOG
   - CTA READS "ADD TO CART" AND NOTHING ELSE. selectVariant used to append
     the chosen size to the label, so the button became "Add to Basket · UK 8"
     the moment a size was picked. That is where the size indicator was coming
     from; the section template never had it. Wording is now "Add to Cart"
     everywhere, including the two restore-after-add paths and the section
     template.
     NOTE: the size still appears in the STICKY BAR subtitle, which is a
     separate element and reads as useful context there rather than as part of
     a button label. Say the word if that should go too.
   - The cart drawer now opens automatically after a successful add.
     Previously the add was deliberately silent: it refreshed the drawer's
     contents but never opened it. Set OPEN_DRAWER_ON_ADD to false below to
     return to the silent behaviour.
   - Fly-to-cart animation removed. The drawer opening is now the confirmation
     that something was added, so the flying badge was redundant.
   - Add is now a single request. The drawer markup comes back with the
     /cart/add.js response and is patched in BEFORE the drawer opens, so it
     slides in already populated instead of opening stale and re-rendering.
     The section was previously requested as 'cart-drawer', but the real
     section id is 'cart-drawer-section', so that markup was never usable and
     a second fetch was doing the work visibly.
   ============================================================ */
(function () {
  // Open the cart drawer after a successful add to cart.
  var OPEN_DRAWER_ON_ADD = true;
  // Delay before opening, in ms. Set to 0 so the drawer opens immediately.
  // The drawer refreshes its own contents a moment later, so it fills in
  // rather than making the shopper wait for it to appear.
  var OPEN_DRAWER_DELAY = 0;
  // Section id of the cart drawer. This is the id on the wrapper element
  // (<div id="shopify-section-cart-drawer-section">), not the snippet filename.
  var CART_DRAWER_SECTION_ID = 'cart-drawer-section';
  // Single source of truth for the button label, so it cannot drift between
  // the initial render, the variant swap and the two restore paths.
  var CTA_LABEL = 'Add to Cart';

  document.querySelectorAll('.ss-pdp').forEach(initPDP);

  /* ---- Open Horizon's cart drawer -------------------------------------
     The drawer is <theme-drawer id="cart-drawer"> wrapping a native <dialog>.
     Which method is available depends on the theme version, so this tries the
     component's own API first and falls back to the dialog element itself.
     Each step returns as soon as one works. */
  function openCartDrawer() {
    var drawer = document.getElementById('cart-drawer') ||
                 document.querySelector('theme-drawer#cart-drawer') ||
                 document.querySelector('theme-drawer');
    if (!drawer) return false;

    // 1. Methods the theme-drawer component may expose.
    var methods = ['open', 'show', 'showDrawer', 'openDrawer', 'toggle'];
    for (var i = 0; i < methods.length; i++) {
      if (typeof drawer[methods[i]] === 'function') {
        try {
          drawer[methods[i]]();
          if (isDrawerOpen(drawer)) return true;
        } catch (e) { /* try the next one */ }
      }
    }

    // 2. Horizon's declarative handlers use "#cart-drawer/close", which implies
    //    a matching open action on the component. Fire it as a custom event.
    try {
      drawer.dispatchEvent(new CustomEvent('open', { bubbles: true }));
      if (isDrawerOpen(drawer)) return true;
    } catch (e) {}

    // 3. Fall back to the native dialog.
    var dialog = drawer.querySelector('dialog[ref="panel"]') || drawer.querySelector('dialog');
    if (dialog && !dialog.open) {
      try {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        return true;
      } catch (e) {
        try { dialog.setAttribute('open', ''); return true; } catch (e2) {}
      }
    }

    return false;
  }

  function isDrawerOpen(drawer) {
    var dialog = drawer.querySelector('dialog');
    if (dialog && dialog.open) return true;
    return drawer.hasAttribute('open');
  }

  /* ---- Patch the drawer from the add response ---------------------------
     /cart/add.js returns rendered section HTML for whatever sections were
     requested. Swapping that in before the drawer opens avoids the second
     fetch and the visible re-render that came with it.
     Returns true if the drawer was patched. */
  function patchDrawerFromSections(data) {
    if (!data || !data.sections) return false;
    var html = data.sections[CART_DRAWER_SECTION_ID];
    if (!html) return false;

    var live = document.querySelector('cart-drawer-component');
    if (!live) return false;

    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('cart-drawer-component');
      if (!fresh) return false;

      live.innerHTML = fresh.innerHTML;
      live.classList.remove('is-empty');

      // Custom elements inside the swapped markup (free-shipping-bar,
      // cart-recommendations) upgrade themselves on insertion, so their
      // connectedCallback runs and they populate without extra work here.
      if (typeof live.renderContents === 'function') {
        try { live.renderContents({}); } catch (e) {}
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  function initPDP(root) {
    var form      = root.querySelector('#ss-product-form');
    var idInput   = root.querySelector('[data-variant-id]');
    var priceEl   = root.querySelector('[data-price]');
    var addBtn    = root.querySelector('[data-add-btn]');
    var ctaText   = root.querySelector('[data-cta-text]');
    var sizeBtns  = root.querySelectorAll('.ss-sizebtn');
    var variants  = readJSON(root.querySelector('[data-ss-variants]'));

    // ---- AJAX add to cart ----
    // Bind to the button's click in the CAPTURE phase and stop propagation so
    // Horizon's own product-form component can't also handle (or hijack) it.
    if (addBtn) {
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        doAdd();
      }, true);
    }
    // Also catch a normal form submit as a backstop (e.g. Enter key).
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        doAdd();
      }, true);
    }

    var adding = false;
    function doAdd() {
      if (adding) return;
      if (addBtn && addBtn.disabled) return;
      // Build the id from the hidden input (kept in sync by size selection).
      var variantId = idInput ? idInput.value : null;
      if (!variantId) { return; }
      adding = true;

      var original = ctaText ? ctaText.textContent : '';
      if (ctaText) ctaText.textContent = 'Adding…';
      if (addBtn) addBtn.disabled = true;

      // Section ids must match the rendered section, not the snippet name.
      // The drawer wrapper is <div id="shopify-section-cart-drawer-section">.
      var body = {
        items: [{ id: variantId, quantity: 1 }],
        sections: CART_DRAWER_SECTION_ID,
        sections_url: window.location.pathname
      };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      })
      .then(function (data) {
        if (ctaText) ctaText.textContent = 'Added ✓';

        // Patch the drawer from the markup that came back with THIS request,
        // then open it. Doing it in that order is what makes the transition
        // feel smooth: the drawer is correct before it is ever visible.
        var patched = patchDrawerFromSections(data);

        if (OPEN_DRAWER_ON_ADD) {
          if (OPEN_DRAWER_DELAY > 0) {
            setTimeout(function () { openCartDrawer(); }, OPEN_DRAWER_DELAY);
          } else {
            openCartDrawer();
          }
        }

        // Header count and any listening components. Runs after the drawer is
        // already on screen, so it never causes a visible re-render.
        updateCart(data, patched);

        setTimeout(function () {
          if (ctaText) ctaText.textContent = CTA_LABEL;
          if (addBtn) addBtn.disabled = false;
          adding = false;
        }, 1400);
      })
      .catch(function (err) {
        if (ctaText) ctaText.textContent = (err && err.description) ? err.description : 'Unavailable';
        setTimeout(function () {
          if (ctaText) ctaText.textContent = CTA_LABEL;
          if (addBtn) addBtn.disabled = false;
          adding = false;
        }, 1800);
      });
    }

    // Update the cart UI without a page refresh.
    // Horizon renders the count inside a <cart-icon> web component (cart-icon.js)
    // via the 'cart-bubble' snippet. We fire the events its JS listens for, then
    // re-render the header cart markup as a guaranteed fallback.
    function updateCart(addData, alreadyPatched) {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var count = cart.item_count;

          // (A) Events Horizon's components listen for.
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              data: { itemCount: count, source: 'product-form-component' },
              source: 'product-form-component',
              itemCount: count,
              sections: (addData && addData.sections) || {}
            }
          }));
          try {
            import('@theme/events').then(function (mod) {
              if (mod && mod.CartUpdateEvent) {
                document.dispatchEvent(new mod.CartUpdateEvent(cart, 'ss-pdp', {
                  itemCount: count, source: 'ss-pdp', sections: (addData && addData.sections) || {}
                }));
              }
            }).catch(function () {});
          } catch (e) {}
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

          // (B) Guaranteed fallback: re-render the header so <cart-icon> markup is fresh,
          //     and mark it as having a cart so the bubble shows.
          var cartIcon = document.querySelector('cart-icon');
          if (cartIcon) {
            if (count > 0) cartIcon.classList.add('header-actions__cart-icon--has-cart');
            else cartIcon.classList.remove('header-actions__cart-icon--has-cart');
          }
          refreshHeaderCart(count);
          // Only fall back to a second fetch if the add response did not
          // carry usable drawer markup.
          if (!alreadyPatched) refreshCartDrawer();
        })
        .catch(function () {});
    }

    // Re-render the cart drawer's contents so it shows the items without a page
    // refresh. Horizon's drawer is a <cart-drawer-component>; we pull its section
    // fresh and swap the inner markup, then let its own JS re-bind.
    function refreshCartDrawer() {
      var drawer = document.querySelector('cart-drawer-component, cart-drawer');
      if (!drawer) return;
      var wrapper = drawer.closest('.shopify-section');
      var sectionId = wrapper ? wrapper.id.replace('shopify-section-', '') : 'cart-drawer';

      fetch('/?section_id=' + sectionId)
        .then(function (r) { return r.ok ? r.text() : Promise.reject(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('cart-drawer-component, cart-drawer');
          if (fresh && drawer) {
            drawer.innerHTML = fresh.innerHTML;
            drawer.classList.remove('is-empty');
            // If the theme exposes a render hook, use it too.
            if (typeof drawer.renderContents === 'function') {
              try { drawer.renderContents({}); } catch (e) {}
            }
          }
        })
        .catch(function () {});
    }

    // Pull the header section fresh and replace the <cart-icon> node so the
    // count bubble updates even if the theme's own JS didn't catch the event.
    function refreshHeaderCart(count) {
      // Find which shopify-section wraps the header so we can re-render it.
      var headerSection = document.querySelector('cart-icon');
      var wrapper = headerSection ? headerSection.closest('.shopify-section') : null;
      var sectionId = wrapper ? wrapper.id.replace('shopify-section-', '') : 'header';

      fetch('/?section_id=' + sectionId)
        .then(function (r) { return r.ok ? r.text() : Promise.reject(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var freshIcon = doc.querySelector('cart-icon');
          var liveIcon  = document.querySelector('cart-icon');
          if (freshIcon && liveIcon) {
            // Replace only the inner bubble/count, not the whole custom element
            // (so we don't detach its event listeners).
            liveIcon.innerHTML = freshIcon.innerHTML;
            liveIcon.className = freshIcon.className;
          }
        })
        .catch(function () {});
    }

    // Kept for backward compat; not used for Horizon's cart-icon.
    function replaceSection() {}

    // ---- gallery: desktop thumbnail swap + mobile swipe carousel + shared lightbox ----
    (function initGallery() {
      var mainImg = root.querySelector('[data-ss-mainimg]');
      var thumbs  = Array.prototype.slice.call(root.querySelectorAll('[data-ss-thumb]'));
      var mSlides = Array.prototype.slice.call(root.querySelectorAll('[data-ss-mslide]'));
      var lb      = root.querySelector('[data-ss-lightbox]');
      if (!lb) return;
      var lbImg   = lb.querySelector('[data-ss-lbimg]');
      var lbClose = lb.querySelector('[data-ss-lbclose]');
      var lbPrev  = lb.querySelector('[data-ss-lbprev]');
      var lbNext  = lb.querySelector('[data-ss-lbnext]');

      // Build the ordered zoom list. Prefer the mobile slides (they list ALL media
      // in order: 0..n). Fall back to desktop main+thumbs. Both produce the same
      // ordered set, so lightbox indices line up whichever gallery is on screen.
      var zoomList = [];
      if (mSlides.length) {
        zoomList = mSlides.map(function (s) { return s.getAttribute('data-zoom'); });
      } else if (mainImg) {
        zoomList = [mainImg.getAttribute('data-zoom')].concat(
          thumbs.map(function (t) { return t.getAttribute('data-zoom'); })
        );
      }
      var current = 0; // index into zoomList
      var lbMoved = false; // true when a touch turned into a drag/swipe (guards close-on-tap)

      // ---- DESKTOP: thumbnail + main-image clicks open the lightbox ----
      if (mainImg) {
        mainImg.style.cursor = 'zoom-in';
        mainImg.addEventListener('click', function () { openLightbox(current); });
      }
      thumbs.forEach(function (thumb, i) {
        thumb.style.cursor = 'zoom-in';
        thumb.addEventListener('click', function () { openLightbox(i + 1); });
      });

      // ---- MOBILE: tap a slide to open the lightbox at that image ----
      mSlides.forEach(function (slide) {
        slide.addEventListener('click', function () {
          var idx = parseInt(slide.getAttribute('data-index'), 10) || 0;
          openLightbox(idx);
        });
      });

      function openLightbox(i) {
        if (!zoomList.length) return;
        current = (i + zoomList.length) % zoomList.length;
        lbImg.src = zoomList[current];
        lb.hidden = false;
        document.body.style.overflow = 'hidden';
      }
      function closeLightbox() {
        lb.hidden = true;
        document.body.style.overflow = '';
      }
      function step(dir) { openLightbox(current + dir); }

      if (lbClose) lbClose.addEventListener('click', closeLightbox);
      if (lbPrev)  lbPrev.addEventListener('click', function () { step(-1); });
      if (lbNext)  lbNext.addEventListener('click', function () { step(1); });
      lb.addEventListener('click', function (e) {
        if (lbMoved) { lbMoved = false; return; } // ignore the click that ends a swipe
        if (e.target === lb) closeLightbox();
      });
      document.addEventListener('keydown', function (e) {
        if (lb.hidden) return;
        if (e.key === 'Escape') closeLightbox();
        else if (e.key === 'ArrowLeft') step(-1);
        else if (e.key === 'ArrowRight') step(1);
      });

      if (zoomList.length < 2) {
        if (lbPrev) lbPrev.style.display = 'none';
        if (lbNext) lbNext.style.display = 'none';
      }

      // ---- MOBILE carousel: legacy dot sync ----
      // The visible dots are now [data-ss-gdot], rendered and driven by the
      // section template, which measures real slide positions. This block still
      // targets the OLD hidden [data-ss-mdots] row, so it writes to something
      // invisible and the two cannot fight. Left in place deliberately: it is
      // harmless, and deleting it means anything else still pointing at the old
      // dots breaks silently.
      (function initMobileCarousel() {
        var track = root.querySelector('[data-ss-mtrack]');
        var dotsWrap = root.querySelector('[data-ss-mdots]');
        if (!track || !mSlides.length) return;
        var dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.querySelectorAll('.ss-mdot')) : [];

        function setActive(idx) {
          dots.forEach(function (d, i) { d.classList.toggle('on', i === idx); });
        }

        // Update the active dot as the track scrolls (debounced via rAF).
        var ticking = false;
        track.addEventListener('scroll', function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(function () {
            var idx = Math.round(track.scrollLeft / track.clientWidth);
            if (idx < 0) idx = 0;
            if (idx > mSlides.length - 1) idx = mSlides.length - 1;
            setActive(idx);
            ticking = false;
          });
        }, { passive: true });

        // Tap a dot to jump to that slide.
        dots.forEach(function (dot, i) {
          dot.style.cursor = 'pointer';
          dot.addEventListener('click', function () {
            track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
            setActive(i);
          });
        });
      })();

      // ---- swipe left/right inside the lightbox (mobile) ----
      // Bound to the whole overlay (not just the image) so a flick anywhere in
      // the zoomed view browses to the next/previous image. lbMoved guards the
      // tap-outside-to-close handler so a swipe doesn't also close the lightbox.
      var swipeStartX = 0, swipeStartY = 0, swiping = false;
      var SWIPE_MIN = 40; // px of horizontal travel to count as a swipe
      lb.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) return;
        swipeStartX = e.touches[0].clientX;
        swipeStartY = e.touches[0].clientY;
        swiping = true;
        lbMoved = false;
      }, { passive: true });
      lb.addEventListener('touchmove', function (e) {
        if (!swiping) return;
        var dx = e.touches[0].clientX - swipeStartX;
        var dy = e.touches[0].clientY - swipeStartY;
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) lbMoved = true;
        // if it's mostly a vertical move, cancel (let it scroll/close naturally)
        if (Math.abs(dy) > Math.abs(dx)) swiping = false;
      }, { passive: true });
      lb.addEventListener('touchend', function (e) {
        if (!swiping) return;
        swiping = false;
        var dx = e.changedTouches[0].clientX - swipeStartX;
        if (dx <= -SWIPE_MIN) step(1);        // swipe left → next
        else if (dx >= SWIPE_MIN) step(-1);   // swipe right → previous
      }, { passive: true });
    })();

    // ---- size / variant selection ----
    sizeBtns.forEach(function (btn) {
      if (btn.disabled) return;
      btn.addEventListener('click', function () {
        sizeBtns.forEach(function (b) { b.classList.remove('sel'); });
        btn.classList.add('sel');
        var vid = btn.getAttribute('data-variant');
        if (vid) selectVariant(vid, btn);
      });
    });

    function selectVariant(vid, btn) {
      if (idInput) idInput.value = vid;
      var v = findVariant(vid);
      if (v && priceEl) priceEl.textContent = formatMoney(v.price);
      // THE LABEL NEVER CHANGES. It used to become
      // "Add to Basket  ·  UK 8" here, which is where the size in the button
      // was coming from. The selected size is already obvious from the
      // highlighted box directly above the button, and it is repeated in the
      // sticky bar subtitle below.
      if (ctaText) ctaText.textContent = CTA_LABEL;
      if (addBtn) addBtn.disabled = v ? !v.available : false;
      // keep sticky bar in sync
      var sbSub = root.querySelector('[data-sb-sub]');
      var sbPrice = root.querySelector('[data-sb-price]');
      if (sbSub && btn) sbSub.textContent = btn.querySelector('.ss-szn').textContent.trim();
      if (sbPrice && v) sbPrice.textContent = formatMoney(v.price);
    }

    function findVariant(vid) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(vid)) return variants[i];
      }
      return null;
    }

    // ---- sticky checkout bar (show once buy box scrolls past) ----
    var sbar   = root.querySelector('[data-sticky-bar]');
    var buyBox = root.querySelector('.ss-buy');
    if (sbar && buyBox) {
      sbar.hidden = false;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          sbar.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0);
        });
      }, { threshold: 0 });
      io.observe(buyBox);

      var sbAdd = root.querySelector('[data-sb-add]');
      if (sbAdd) sbAdd.addEventListener('click', function () {
        if (addBtn && !addBtn.disabled) {
          doAdd();
        } else {
          buyBox.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }

    // ---- helpers ----
    function formatMoney(cents) {
      // Uses the theme's active currency format if available.
      var fmt = (window.Shopify && Shopify.money_format) || '£{{amount}}';
      var amount = (cents / 100).toFixed(2);
      return fmt.replace(/\{\{\s*amount\s*\}\}/, amount)
                .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
    }
    function readJSON(el) { try { return JSON.parse(el.textContent) || []; } catch (e) { return []; } }
  }
})();